/*
 * Run something once the page has loaded and its pictures are on screen —
 * the moment for a script the shopper does not see.
 *
 * 2026-10: Meta's pixel (fbevents.js and its per-pixel config, 940 KB
 * decoded, 260 KB over the wire) was inserted the moment the shop's payload
 * named a pixel, and shared a phone's cellular line with the product photo
 * and the script that draws the buy button. It now waits for `load` — and,
 * since the product page's photos are asked for only after the app renders,
 * which on a slow line is after `load` — until every image the page has asked
 * for has arrived or failed. A ceiling keeps a page whose `load` never comes,
 * or whose one image hangs, reporting all the same. A page that has already
 * loaded with its pictures in place runs it at once, which is also what every
 * test environment sees.
 */
const IMAGE_POLL_MS = 250;

/**
 * Whether every picture the page has asked for has arrived or failed. One the
 * browser was told to fetch lazily is not asked for until it nears the
 * viewport, so it is not waited on: it costs the line nothing yet, and a
 * carousel's far frames would otherwise hold the pixel until the ceiling.
 */
function imagesSettled() {
  const images = document.images;
  for (let i = 0; i < images.length; i += 1) {
    if (!images[i].complete && images[i].loading !== "lazy") return false;
  }
  return true;
}

/*
 * THE PICTURES ARRIVE AFTER `load` (measured 2026-10-01, 4G profile): the app
 * renders at 3.9 s, `load` fired at 3.5 s with not one product photograph on
 * the page yet, and the pixel's 110 KB went down the line beside the hero. So
 * "settled" also means nothing new was asked for in the last QUIET_MS — a
 * page that keeps adding pictures is still painting.
 */
const QUIET_MS = 1500;

export function afterPageLoad(run, { ceilingMs = 10000 } = {}) {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  let done = false;
  let poll;
  const once = () => {
    if (done) return;
    done = true;
    window.clearTimeout(poll);
    window.clearTimeout(ceiling);
    run();
  };
  // The one timer nothing else clears: whatever `load` and the pictures do, this runs.
  const ceiling = window.setTimeout(once, ceilingMs);
  // A page that is still loading when asked is a cold load: its pictures come after `load`, so the quiet is counted
  // from `load`. A page already complete when asked (a route change, every test) runs at once unless a picture is
  // then added — only that restarts the quiet.
  const cold = document.readyState !== "complete";
  // The app asks after `load` on a slow line too (it renders at 3.9 s, `load` fired at 3.5 s): a page whose `load`
  // is less than QUIET_MS old is still painting, and gets the same quiet. A navigation entry jsdom does not keep
  // reads as "loaded long ago", so a test page that is complete runs at once, as before.
  const loadedAgoMs = (() => {
    try {
      const nav = performance.getEntriesByType("navigation")[0];
      return nav && nav.loadEventEnd > 0 ? performance.now() - nav.loadEventEnd : Infinity;
    } catch (_) { return Infinity; }
  })();
  const stillPainting = cold || loadedAgoMs < QUIET_MS;
  let seen = null;
  let changedAt = 0;
  const whenImagesSettle = () => {
    if (done) return;
    const count = document.images.length;
    if (seen === null) { seen = count; if (stillPainting) changedAt = Date.now(); }
    else if (count !== seen) { seen = count; changedAt = Date.now(); }
    if (imagesSettled() && Date.now() - changedAt >= QUIET_MS) once();
    else poll = window.setTimeout(whenImagesSettle, IMAGE_POLL_MS);
  };
  if (document.readyState === "complete") whenImagesSettle();
  else window.addEventListener("load", whenImagesSettle, { once: true });
}
